import React, { useState, useRef, useEffect } from 'react';
import { useAcademicYear } from '../context/AcademicYearContext';
import { getAcademicYearRange } from '../services/academicYearService';
import { Calendar, ChevronDown, Check, Sparkles, Archive } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface Props {
  compact?: boolean;
}

export const AcademicYearSelector: React.FC<Props> = ({ compact = false }) => {
  const { selectedYear, setSelectedYear, academicYears, currentYearObj } = useAcademicYear();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isCurrentNewYear = selectedYear === '2026-2027';

  return (
    <div className="relative inline-block text-right" ref={dropdownRef} dir="rtl">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2.5 px-3.5 py-2 rounded-xl transition-all duration-200 border ${
          isCurrentNewYear
            ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-900 border-indigo-200 shadow-sm hover:border-indigo-300'
            : 'bg-amber-50 text-amber-900 border-amber-200 hover:border-amber-300'
        }`}
        title="בחר שנת לימודים"
      >
        <div className={`p-1.5 rounded-lg ${isCurrentNewYear ? 'bg-indigo-600 text-white' : 'bg-amber-600 text-white'}`}>
          <Calendar size={compact ? 14 : 16} />
        </div>

        <div className="flex flex-col text-right">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              שנת לימודים:
            </span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
              isCurrentNewYear ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
            }`}>
              {isCurrentNewYear ? 'שנה נוכחית' : 'ארכיון'}
            </span>
          </div>
          <span className="text-xs md:text-sm font-bold text-slate-800">
            {currentYearObj.name}
          </span>
        </div>

        <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 mr-1 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 md:right-auto md:w-64 mt-2 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-50 overflow-hidden"
          >
            <div className="px-4 py-2 border-b border-slate-50 bg-slate-50/50">
              <p className="text-[11px] font-bold text-slate-500">בחירת שנת לימודים</p>
              <p className="text-[10px] text-slate-400">הכיתות, הצוות והתלמידים יוצגו לפי השנה הנבחרת</p>
            </div>

            <div className="p-1 space-y-1">
              {academicYears.map((year) => {
                const isSelected = year.id === selectedYear;
                const isNew = year.id === '2026-2027';

                return (
                  <button
                    key={year.id}
                    onClick={() => {
                      setSelectedYear(year.id);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-right text-xs transition-colors ${
                      isSelected
                        ? 'bg-indigo-50 text-indigo-900 font-bold'
                        : 'text-slate-700 hover:bg-slate-50 font-medium'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`p-1 rounded-lg ${isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                        {isNew ? <Sparkles size={14} /> : <Archive size={14} />}
                      </div>
                      <div>
                        <div className="font-bold">{year.name}</div>
                        <div className="text-[10px] text-slate-400">
                          {isNew ? 'שנת הלימודים הפעילה' : 'נתוני שנה קודמת'} • {getAcademicYearRange(year.id).startDate} עד {getAcademicYearRange(year.id).endDate}
                        </div>
                      </div>
                    </div>
                    {isSelected && <Check size={16} className="text-indigo-600" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
